import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const WEEKDAY_KEYS = [
    'calendar.weekday.sun', 'calendar.weekday.mon', 'calendar.weekday.tue',
    'calendar.weekday.wed', 'calendar.weekday.thu', 'calendar.weekday.fri',
    'calendar.weekday.sat',
];

/**
 * LeadsCalendar — month grid 顯示預約類 leads
 *
 * Props:
 *   leads          — array of { id, collectionData, createdAt, ... }
 *   dateFieldName  — 預約日期欄位名（從 scenario.fields 偵測，例：'appointment_date'）
 *   timeFieldName  — 預約時段欄位名（optional，例：'appointment_time'）
 *
 * 在月格內顯示「YYYY-MM-DD」對應的 lead 數（dot），點某天 → 右側 popup 詳細名單。
 */
export function LeadsCalendar({ leads = [], dateFieldName, timeFieldName }) {
    const { t } = useTranslation('leads');
    const [cursor, setCursor] = useState(() => {
        const d = new Date();
        return { year: d.getFullYear(), month: d.getMonth() };  // month 0-11
    });
    const [selectedDay, setSelectedDay] = useState(null);

    // group leads by YYYY-MM-DD from collectionData[dateFieldName]
    const leadsByDay = useMemo(() => {
        const map = {};
        leads.forEach((lead) => {
            const dateStr = lead.collectionData?.[dateFieldName];
            if (!dateStr) return;
            // 嘗試 normalize：支援 "2026-05-13"、"5/13"、"5月13日" 等
            const key = normalizeDateKey(dateStr, cursor.year);
            if (!key) return;
            if (!map[key]) map[key] = [];
            map[key].push(lead);
        });
        return map;
    }, [leads, dateFieldName, cursor.year]);

    const firstOfMonth = new Date(cursor.year, cursor.month, 1);
    const lastOfMonth = new Date(cursor.year, cursor.month + 1, 0);
    const daysInMonth = lastOfMonth.getDate();
    const firstWeekday = firstOfMonth.getDay();

    // build calendar cells (6 weeks max)
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    const goPrev = () => {
        if (cursor.month === 0) setCursor({ year: cursor.year - 1, month: 11 });
        else setCursor({ ...cursor, month: cursor.month - 1 });
    };
    const goNext = () => {
        if (cursor.month === 11) setCursor({ year: cursor.year + 1, month: 0 });
        else setCursor({ ...cursor, month: cursor.month + 1 });
    };
    const goToday = () => {
        const d = new Date();
        setCursor({ year: d.getFullYear(), month: d.getMonth() });
    };

    const today = new Date();
    const isToday = (d) =>
        d &&
        cursor.year === today.getFullYear() &&
        cursor.month === today.getMonth() &&
        d === today.getDate();

    const monthLabel = t('calendar.yearMonth', { year: cursor.year, month: cursor.month + 1 });

    const selectedDayLeads = selectedDay
        ? leadsByDay[dayKey(cursor.year, cursor.month, selectedDay)] || []
        : [];

    return (
        <div className="bg-white border border-cream-200 rounded-md overflow-hidden">
            {/* Month nav */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-cream-200 bg-cream-50/40">
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={goPrev}
                        className="size-7 inline-flex items-center justify-center rounded-md text-ink-600 hover:text-ink-900 hover:bg-cream-100 transition"
                    >
                        <ChevronLeft size={14} />
                    </button>
                    <h3 className="heading-display text-base font-semibold text-ink-900 px-2 tabular-nums">
                        {monthLabel}
                    </h3>
                    <button
                        onClick={goNext}
                        className="size-7 inline-flex items-center justify-center rounded-md text-ink-600 hover:text-ink-900 hover:bg-cream-100 transition"
                    >
                        <ChevronRight size={14} />
                    </button>
                </div>
                <button
                    onClick={goToday}
                    className="font-mono text-[10px] uppercase tracking-wider text-ink-500 hover:text-accent-700 transition px-2 py-1 rounded"
                >
                    {t('calendar.today')}
                </button>
            </div>

            <div className="flex">
                {/* Calendar grid */}
                <div className="flex-1 p-3">
                    {/* Weekday header */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {WEEKDAY_KEYS.map((wKey, i) => (
                            <div
                                key={wKey}
                                className={[
                                    'text-center font-mono text-[10px] uppercase tracking-wider',
                                    i === 0 || i === 6 ? 'text-accent-700' : 'text-ink-400',
                                ].join(' ')}
                            >
                                {t(wKey)}
                            </div>
                        ))}
                    </div>

                    {/* Day cells */}
                    <div className="grid grid-cols-7 gap-1">
                        {cells.map((d, i) => {
                            if (!d) return <div key={i} className="min-h-[88px]" />;
                            const key = dayKey(cursor.year, cursor.month, d);
                            const dayLeads = (leadsByDay[key] || [])
                                .slice()
                                .sort((a, b) => {
                                    const at = a.collectionData?.[timeFieldName] || '';
                                    const bt = b.collectionData?.[timeFieldName] || '';
                                    return String(at).localeCompare(String(bt));
                                });
                            const hasLeads = dayLeads.length > 0;
                            const isSelected = selectedDay === d;
                            const visible = dayLeads.slice(0, 2);
                            const extra = dayLeads.length - visible.length;
                            return (
                                <button
                                    key={i}
                                    onClick={() => setSelectedDay(d)}
                                    className={[
                                        'min-h-[88px] relative flex flex-col p-1.5 rounded-md text-left transition border',
                                        isSelected
                                            ? 'bg-accent-700 text-cream-50 border-accent-700'
                                            : isToday(d)
                                                ? 'bg-cream-100 text-ink-900 border-accent-400'
                                                : hasLeads
                                                    ? 'bg-cream-50 text-ink-900 border-cream-200 hover:border-accent-300'
                                                    : 'text-ink-500 border-transparent hover:bg-cream-50',
                                    ].join(' ')}
                                >
                                    <span className={[
                                        'text-sm tabular-nums',
                                        isSelected ? '' : isToday(d) ? 'font-semibold' : '',
                                    ].join(' ')}>
                                        {d}
                                    </span>
                                    {hasLeads && (
                                        <div className="mt-1 flex flex-col gap-0.5 w-full">
                                            {visible.map((lead) => {
                                                const tm = timeFieldName && lead.collectionData?.[timeFieldName];
                                                const nm = lead.collectionData?.name || '—';
                                                return (
                                                    <div
                                                        key={lead.id}
                                                        className={[
                                                            'text-[10px] leading-tight truncate px-1 py-0.5 rounded',
                                                            isSelected
                                                                ? 'bg-cream-50/20 text-cream-50'
                                                                : 'bg-accent-50 text-accent-800',
                                                        ].join(' ')}
                                                        title={`${tm || ''} ${nm}`.trim()}
                                                    >
                                                        {tm && <span className="tabular-nums mr-0.5">{tm}</span>}
                                                        <span className="font-medium">{nm}</span>
                                                    </div>
                                                );
                                            })}
                                            {extra > 0 && (
                                                <div className={[
                                                    'text-[9px] font-mono px-1',
                                                    isSelected ? 'text-cream-50' : 'text-accent-700',
                                                ].join(' ')}>
                                                    {t('calendar.more', { count: extra })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Day detail side panel */}
                {selectedDay && (
                    <div className="w-72 border-l border-cream-200 bg-cream-50/40 p-3 flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
                                    {dayKey(cursor.year, cursor.month, selectedDay)}
                                </div>
                                <div className="text-sm font-medium text-ink-900 mt-0.5">
                                    {t('calendar.bookingCount', { count: selectedDayLeads.length })}
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedDay(null)}
                                className="size-6 inline-flex items-center justify-center text-ink-400 hover:text-ink-700 hover:bg-cream-100 rounded transition"
                            >
                                <X size={13} />
                            </button>
                        </div>

                        {selectedDayLeads.length === 0 ? (
                            <div className="text-xs text-ink-400 italic mt-2">{t('calendar.noBooking')}</div>
                        ) : (
                            <div className="space-y-2 overflow-y-auto flex-1">
                                {selectedDayLeads
                                    .slice()
                                    .sort((a, b) => {
                                        const at = a.collectionData?.[timeFieldName] || '';
                                        const bt = b.collectionData?.[timeFieldName] || '';
                                        return String(at).localeCompare(String(bt));
                                    })
                                    .map((lead) => (
                                        <div
                                            key={lead.id}
                                            className="bg-white border border-cream-200 rounded-md p-2.5 text-xs"
                                        >
                                            {timeFieldName && lead.collectionData?.[timeFieldName] && (
                                                <div className="font-mono text-[11px] text-accent-700 mb-1 tabular-nums">
                                                    {lead.collectionData[timeFieldName]}
                                                </div>
                                            )}
                                            <div className="font-medium text-ink-900">
                                                {lead.collectionData?.name || '—'}
                                            </div>
                                            {lead.collectionData?.phone && (
                                                <div className="text-ink-500 mt-0.5">
                                                    {lead.collectionData.phone}
                                                </div>
                                            )}
                                            {Object.entries(lead.collectionData || {})
                                                .filter(([k]) => !['name', 'phone', dateFieldName, timeFieldName].includes(k))
                                                .filter(([, v]) => v)
                                                .map(([k, v]) => (
                                                    <div key={k} className="text-ink-400 text-[10.5px] mt-0.5">
                                                        {k}: {String(v).substring(0, 30)}
                                                    </div>
                                                ))}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function dayKey(year, month, day) {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function normalizeDateKey(raw, defaultYear) {
    if (!raw) return null;
    const s = String(raw).trim();

    // YYYY-MM-DD
    let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (m) {
        return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
    }

    // M/D 或 MM/DD
    m = s.match(/^(\d{1,2})[/-](\d{1,2})/);
    if (m) {
        return `${defaultYear}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
    }

    // YYYY年M月D日 / M月D日
    m = s.match(/^(?:(\d{4})年)?(\d{1,2})月(\d{1,2})日/);
    if (m) {
        const y = m[1] || defaultYear;
        return `${y}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
    }

    return null;
}
